import { Html, Line } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useAppStore } from '@store'
import { Item, LinkedItem } from '@types'
import { useMemo, useRef } from 'react'
import { Quaternion, Vector3 } from 'three'

type DimensionObject = {
  id: string | number
  type?: string
  idUnidades?: number
  catalogID?: string | number
  referencia?: string
  matrix?: {
    decompose: (position: Vector3, quaternion: Quaternion, scale: Vector3) => void
  }
  width?: number
  height?: number
  thickness?: number
  canCollide?: boolean
}

type DimensionsBBox = {
  minX: number
  minY: number
  minZ: number
  maxX: number
  maxY: number
  maxZ: number
}

type DimensionsLayout = {
  size: Vector3
  bbox: DimensionsBBox
  widthPosition: [number, number, number]
  heightPosition: [number, number, number]
  depthPosition: [number, number, number]
  anchorPosition: Vector3
  anchorQuaternion: Quaternion
}

function isDimensionObject(value: unknown): value is DimensionObject {
  if (!value || typeof value !== 'object') return false
  const maybe = value as Partial<DimensionObject>
  return !!maybe.matrix && typeof maybe.width === 'number' && typeof maybe.height === 'number'
}

function getDimensionItems(items: Map<unknown, unknown>) {
  return Array.from(items.values()).filter(
    (item): item is (Item | LinkedItem | DimensionObject) & DimensionObject => {
      if (!isDimensionObject(item)) return false

      const isSupportedType = item.type === 'items' || item.type === 'linkedItem'

      if (!isSupportedType) return false
      if (!item.width || !item.height) return false
      if (item.canCollide === false) return false

      return true
    },
  )
}

function computeDimensionsLayout(items: Map<unknown, unknown>) {
  const allItemsArray = getDimensionItems(items)
  if (allItemsArray.length === 0) return null

  // Ancla siempre en el primer item dibujado en la escena (orden de inserción del Map)
  const anchorItem = allItemsArray[0]
  if (!anchorItem?.matrix) return null

  const anchorPosition = new Vector3()
  const anchorQuaternion = new Quaternion()
  const anchorScale = new Vector3()
  anchorItem.matrix.decompose(anchorPosition, anchorQuaternion, anchorScale)
  const inverseAnchorQuaternion = anchorQuaternion.clone().invert()

  let minX = Infinity
  let minY = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let maxZ = -Infinity

  for (const item of allItemsArray) {
    if (!item.matrix || !item.width || !item.height) continue

    const position = new Vector3()
    const quaternion = new Quaternion()
    const scale = new Vector3()
    item.matrix.decompose(position, quaternion, scale)

    const itemWidth = item.width || 0
    const itemThickness = item.thickness || 0
    const corners = [
      new Vector3(0, 0, 0),
      new Vector3(itemWidth, 0, 0),
      new Vector3(0, item.height, 0),
      new Vector3(itemWidth, item.height, 0),
      new Vector3(0, 0, itemThickness),
      new Vector3(itemWidth, 0, itemThickness),
      new Vector3(0, item.height, itemThickness),
      new Vector3(itemWidth, item.height, itemThickness),
    ]

    for (const corner of corners) {
      const worldCorner = corner.applyQuaternion(quaternion).add(position)
      const localCorner = worldCorner.sub(anchorPosition).applyQuaternion(inverseAnchorQuaternion)

      if (localCorner.x < minX) minX = localCorner.x
      if (localCorner.y < minY) minY = localCorner.y
      if (localCorner.z < minZ) minZ = localCorner.z
      if (localCorner.x > maxX) maxX = localCorner.x
      if (localCorner.y > maxY) maxY = localCorner.y
      if (localCorner.z > maxZ) maxZ = localCorner.z
    }
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(minZ) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY) ||
    !Number.isFinite(maxZ)
  ) {
    return null
  }

  const GAP = 150
  const size = new Vector3(maxX - minX, maxY - minY, maxZ - minZ)
  const bbox: DimensionsBBox = { minX, minY, minZ, maxX, maxY, maxZ }
  const widthPosition: [number, number, number] = [minX, minY, maxZ + 100]
  const heightPosition: [number, number, number] = [maxX + GAP, minY, minZ]
  const depthPosition: [number, number, number] = [maxX + GAP * 2, minY, minZ]

  return {
    size,
    bbox,
    widthPosition,
    heightPosition,
    depthPosition,
    anchorPosition,
    anchorQuaternion,
  } satisfies DimensionsLayout
}
/**
 * Componente para mostrar cotas (dimensiones) de los items seleccionados
 * Muestra ancho, alto y profundo de forma limpia y profesional
 * IMPORTANTE: Usa la posición inicial donde se dibujó el item (donde está la cámara)
 */
export function ItemDimensions3D() {
  const items = useAppStore.use.items()
  const hideDimentions3D = useAppStore((store) => store.hideDimentions3D ?? false)
  // const unit = useAppStore.use.prefs()?.UNIDADMEDIDA || 'mm'
  const unit = 'cm'
  const PREFS_COLOR_COTA = '#545D67'
  const FONT_SIZE = useAppStore.use.prefs()?.['T/LETRACOTA3D'] ?? 100

  // Referencias para los grupos de las tres cotas
  const dimensionsRootRef = useRef<THREE.Group>(null)
  const widthCotaGroupRef = useRef<THREE.Group>(null)
  const heightCotaGroupRef = useRef<THREE.Group>(null)
  const depthCotaGroupRef = useRef<THREE.Group>(null)

  // Calcular layout de cotas con orientación local al item ancla
  const dimensions = useMemo(() => computeDimensionsLayout(items), [items])

  // Actualizar las posiciones de las tres cotas en cada frame para que se muevan con el GLB
  useFrame(() => {
    if (
      !dimensionsRootRef.current ||
      !widthCotaGroupRef.current ||
      !heightCotaGroupRef.current ||
      !depthCotaGroupRef.current
    )
      return

    const runtimeDimensions = computeDimensionsLayout(items)
    if (!runtimeDimensions) return

    dimensionsRootRef.current.position.copy(runtimeDimensions.anchorPosition)
    dimensionsRootRef.current.quaternion.copy(runtimeDimensions.anchorQuaternion)

    widthCotaGroupRef.current.position.set(...runtimeDimensions.widthPosition)
    heightCotaGroupRef.current.position.set(...runtimeDimensions.heightPosition)
    depthCotaGroupRef.current.position.set(...runtimeDimensions.depthPosition)
  })

  if (hideDimentions3D || !dimensions) return null

  const {
    size,
    bbox,
    widthPosition,
    heightPosition,
    depthPosition,
    anchorPosition,
    anchorQuaternion,
  } = dimensions

  const tickLength = 40 // Longitud de las marcas en los extremos

  return (
    <group
      ref={dimensionsRootRef}
      position={anchorPosition}
      quaternion={anchorQuaternion}
    >
      {/* Cota de ANCHO (X) - línea horizontal delante del objeto */}
      <group
        ref={widthCotaGroupRef}
        position={widthPosition}
        renderOrder={1000}
      >
        {/* Línea horizontal principal - va desde 0 hasta size.x (totalWidth) */}
        <Line
          points={[
            [0, 0, 0], // Extremo izquierdo (en la esquina del objeto)
            [size.x, 0, 0], // Extremo derecho (suma de anchos)
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Líneas verticales en los extremos (tick marks) */}
        <Line
          points={[
            [0, -tickLength / 2, 0],
            [0, tickLength / 2, 0],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        <Line
          points={[
            [size.x, -tickLength / 2, 0],
            [size.x, tickLength / 2, 0],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Texto con fondo negro redondeado usando Html - siempre visible por encima del grid */}
        {(() => {
          const formatDimension = (value: number) => {
            return Math.round(value / 10)
          }
          const text = `${formatDimension(size.x)} ${unit}`
          const textPosition: [number, number, number] = [size.x / 2, -180, 0]
          return (
            <Html
              position={textPosition}
              center
              zIndexRange={[1, 0]}
              style={{
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              wrapperClass='dimension-text-wrapper'
            >
              <div
                className='dimension-text'
                style={{
                  color: '#545D67',
                  fontSize: `${Math.max(12, FONT_SIZE * 0.15)}px`,
                  fontFamily: 'Century Gothic, sans-serif',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {text}
              </div>
            </Html>
          )
        })()}
      </group>

      {/* Cota de ALTO (Y) - línea vertical, restando mitad del ancho del último item */}
      <group
        ref={heightCotaGroupRef}
        position={heightPosition}
        renderOrder={1000}
      >
        {/* Línea vertical principal - va desde 0 hasta size.y (maxHeight) */}
        <Line
          points={[
            [0, 0, 0], // Extremo inferior (en la base del objeto)
            [0, size.y, 0], // Extremo superior (altura máxima)
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Líneas horizontales en los extremos (tick marks) */}
        <Line
          points={[
            [-tickLength / 2, 0, 0],
            [tickLength / 2, 0, 0],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        <Line
          points={[
            [-tickLength / 2, size.y, 0],
            [tickLength / 2, size.y, 0],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Texto con fondo negro redondeado usando Html - siempre visible por encima del grid */}
        {(() => {
          const formatDimension = (value: number) => {
            return Math.round(value / 10)
          }
          const text = `${formatDimension(size.y)} ${unit}`
          const textPosition: [number, number, number] = [180, size.y / 2, 0]
          return (
            <Html
              position={textPosition}
              center
              zIndexRange={[1, 0]}
              style={{
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              wrapperClass='dimension-text-wrapper'
            >
              <div
                className='dimension-text'
                style={{
                  color: '#545D67',
                  fontSize: `${Math.max(12, FONT_SIZE * 0.15)}px`,
                  fontFamily: 'Century Gothic, sans-serif',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {text}
              </div>
            </Html>
          )
        })()}
      </group>

      {/* Cota de LARGO/PROFUNDO (Z) - restando mitad del ancho del último item */}
      <group
        ref={depthCotaGroupRef}
        position={depthPosition}
        renderOrder={1000}
      >
        {/* Línea en profundidad - va desde el mínimo local Z hasta el máximo */}
        <Line
          points={[
            [0, 0, 0],
            [0, 0, size.z],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Líneas de referencia (tick marks) - exactamente en los extremos */}
        <Line
          points={[
            [-tickLength / 2, 0, 0],
            [tickLength / 2, 0, 0],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        <Line
          points={[
            [-tickLength / 2, 0, size.z],
            [tickLength / 2, 0, size.z],
          ]}
          color={PREFS_COLOR_COTA}
          lineWidth={1}
          renderOrder={1000}
        />
        {/* Texto con fondo negro redondeado usando Html - siempre visible por encima del grid */}
        {(() => {
          const formatDimension = (value: number) => {
            return Math.round(value / 10)
          }
          const text = `${formatDimension(size.z)} ${unit}`
          const textPosition: [number, number, number] = [180, 0, size.z / 2]
          return (
            <Html
              position={textPosition}
              center
              zIndexRange={[1, 0]}
              style={{
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              wrapperClass='dimension-text-wrapper'
            >
              <div
                className='dimension-text'
                style={{
                  color: '#545D67',
                  fontSize: `${Math.max(12, FONT_SIZE * 0.15)}px`,
                  fontFamily: 'Century Gothic, sans-serif',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {text}
              </div>
            </Html>
          )
        })()}
      </group>

      {/* Líneas de extensión discontinuas entre el AABB real del objeto y cada cota */}
      <Line
        points={[
          [bbox.minX, bbox.minY, bbox.maxZ],
          [bbox.minX, bbox.minY, widthPosition[2]],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
      <Line
        points={[
          [bbox.maxX, bbox.minY, bbox.maxZ],
          [bbox.maxX, bbox.minY, widthPosition[2]],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
      <Line
        points={[
          [bbox.maxX, bbox.minY, bbox.minZ],
          [heightPosition[0], bbox.minY, bbox.minZ],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
      <Line
        points={[
          [bbox.maxX, bbox.maxY, bbox.minZ],
          [heightPosition[0], bbox.maxY, bbox.minZ],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
      <Line
        points={[
          [bbox.maxX, bbox.minY, bbox.minZ],
          [depthPosition[0], bbox.minY, bbox.minZ],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
      <Line
        points={[
          [bbox.maxX, bbox.minY, bbox.maxZ],
          [depthPosition[0], bbox.minY, bbox.maxZ],
        ]}
        color={PREFS_COLOR_COTA}
        dashed
        dashSize={18}
        gapSize={12}
        lineWidth={0.6}
        renderOrder={1000}
      />
    </group>
  )
}
